import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/Card/HoverCard';
import { DataTableColumnHeader } from '@/components/DataTable/Columns/DataTableColumnHeader';
import type { HelperOptions } from '@/components/DataTable/types';
import { HStack } from '@/components/Layout';
import VStack from '@/components/Layout/VStack';
import { Text } from '@/components/Typography/Text';
import { AvatarApp } from '@/features/app/components/Avatar/AvatarApp';
import { AppIntegrationPreviewContent } from '@/features/app/components/PreviewCard/AppIntegrationPreviewContent';
import { SoftwarePreviewContent } from '@/features/software/components/PreviewCard/SoftwarePreviewContent';

type Row = {
  software: {
    id: unknown;
    name: string;
    logoUrl: string | null;
  };
  appIntegration?: {
    appIntegrationId: string;
    appName: string;
    tenantName?: string;
    avatarUrl?: string;
  };
};

export default function IntegratedAppColumn<TData extends Row>(
  id: keyof TData,
  helperOptions: HelperOptions,
): ColumnDef<TData> {
  const { minWidth } = helperOptions;
  return {
    accessorKey: id,
    meta: { thStyle: { minWidth }, i18nKey: 'software' },
    enableSorting: true,
    header: DataTableColumnHeader,
    cell: ({ row }) => {
      const { software, appIntegration } = row.original;
      const softwareIdStr = String(software.id);

      // SaaS (appIntegration あり) の場合
      if (appIntegration) {
        return (
          <HoverCard openDelay={150} closeDelay={150}>
            <HoverCardTrigger asChild>
              <Link to="/software/$id" params={{ id: softwareIdStr }}>
                <HStack className="gap-2 items-center">
                  <AvatarApp
                    src={appIntegration.avatarUrl}
                    name={appIntegration.appName}
                  />
                  <VStack className="gap-0.5 overflow-hidden">
                    <Text variant="labelSmall" truncate className="max-w-full">
                      {appIntegration.appName}
                    </Text>
                    {appIntegration.tenantName && (
                      <Text
                        variant="labelSmallReg"
                        truncate
                        className="max-w-full text-neutral-on-fill"
                      >
                        {appIntegration.tenantName}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Link>
            </HoverCardTrigger>
            <HoverCardContent className="w-min whitespace-nowrap">
              <AppIntegrationPreviewContent
                appIntegration={{
                  id: appIntegration.appIntegrationId,
                  ...appIntegration,
                }}
              />
            </HoverCardContent>
          </HoverCard>
        );
      }

      // SaaS 以外 (appIntegration なし) の場合
      const softwareId = String(software.id);
      return (
        <HoverCard openDelay={150} closeDelay={150}>
          <HoverCardTrigger asChild>
            <Link to="/software/$id" params={{ id: softwareId }}>
              <HStack className="gap-2 items-center">
                <AvatarApp
                  src={software.logoUrl ?? undefined}
                  name={software.name}
                />
                <VStack className="gap-0.5 overflow-hidden">
                  <Text variant="labelSmall" truncate className="max-w-full">
                    {software.name}
                  </Text>
                </VStack>
              </HStack>
            </Link>
          </HoverCardTrigger>
          <HoverCardContent className="w-min whitespace-nowrap">
            <SoftwarePreviewContent
              software={{
                id: softwareId,
                name: software.name,
                avatarUrl: software.logoUrl ?? undefined,
              }}
            />
          </HoverCardContent>
        </HoverCard>
      );
    },
  } satisfies ColumnDef<TData>;
}
